import { useMemo } from 'react';
import type { Analisis, ResultadoNesting } from '@laser/core';
import { numero, porcentaje } from '../lib/formato';
import { dibujarNesting, disponerHojas } from '../lib/nesting';

// Tonos de identidad por archivo (orden fijo, validados; pasan del 3.º se repite el contorno, no el color).
const TONOS = ['#2a78d6', '#eb6834', '#1baf7a', '#eda100', '#e87ba4', '#4a3aa7'];

export function VistaNesting({ nesting, analisis, colorMaterial, nombres }: {
  nesting: ResultadoNesting; analisis: Record<string, Analisis | null | undefined>; colorMaterial?: string; nombres?: Record<string, string>;
}) {
  const { disp, piezas, archivos } = useMemo(() => {
    const columnas = nesting.hojas <= 1 ? 1 : nesting.hojaAnchoMm > nesting.hojaAltoMm * 1.4 ? 1 : 2;
    const separacion = Math.max(nesting.hojaAnchoMm, nesting.hojaAltoMm) * 0.05;
    const disp = disponerHojas(Math.max(1, nesting.hojas), nesting.hojaAnchoMm, nesting.hojaAltoMm, columnas, separacion);
    const piezas = dibujarNesting(nesting, analisis, disp);
    const archivos = [...new Set(nesting.colocaciones.map((c) => c.archivoId))];
    return { disp, piezas, archivos };
  }, [nesting, analisis]);

  const pad = Math.max(nesting.hojaAnchoMm, nesting.hojaAltoMm) * 0.03;
  const etiqueta = Math.max(nesting.hojaAnchoMm, nesting.hojaAltoMm) * 0.035;
  const tono = (archivoId: string) => TONOS[archivos.indexOf(archivoId) % TONOS.length];

  if (!nesting.hojas) return <p className="muted">Sin piezas colocadas.</p>;

  return (
    <div className="pila" style={{ gap: 8 }}>
      <div className="fila" style={{ gap: 14 }}>
        <span><b className="num">{nesting.hojas}</b> <span className="muted">{nesting.hojas === 1 ? 'hoja' : 'hojas'} de {numero(nesting.hojaAnchoMm)} × {numero(nesting.hojaAltoMm)} mm</span></span>
        <span><b className="num">{porcentaje(nesting.aprovechamiento)}</b> <span className="muted">aprovechamiento</span></span>
        <span><b className="num">{nesting.colocaciones.length}</b> <span className="muted">piezas</span></span>
      </div>
      <div className="nesting">
        <svg viewBox={`${-pad} ${-pad - etiqueta * 1.4} ${disp.anchoTotal + 2 * pad} ${disp.altoTotal + 2 * pad + etiqueta * 1.4}`}
          role="img" aria-label={`Acomodo en ${nesting.hojas} hojas con ${porcentaje(nesting.aprovechamiento)} de aprovechamiento`}>
          {disp.hojas.map((h) => (
            <g key={h.indice}>
              <text x={h.x} y={h.y - etiqueta * 0.4} fontSize={etiqueta} fill="currentColor" style={{ fill: 'var(--muted)' }}>Hoja {h.indice + 1}</text>
              <rect x={h.x} y={h.y} width={nesting.hojaAnchoMm} height={nesting.hojaAltoMm} fill={colorMaterial ?? '#e9dcc7'} fillOpacity={0.55}
                stroke="#667085" strokeWidth={1} vectorEffect="non-scaling-stroke" />
            </g>
          ))}
          {piezas.map((p) => (
            <path key={p.clave} d={p.d} fillRule="evenodd" fill={tono(p.archivoId)} fillOpacity={0.4}
              stroke="#b42318" strokeWidth={1} vectorEffect="non-scaling-stroke">
              <title>{`${nombres?.[p.archivoId] ?? p.archivoId} · pieza ${p.piezaId} · copia ${p.copia + 1} · hoja ${p.hoja + 1}`}</title>
            </path>
          ))}
        </svg>
      </div>
      {archivos.length > 1 && (
        <div className="clave-grafica">
          {archivos.map((a) => <span key={a}><i style={{ background: tono(a), opacity: 0.7 }} />{nombres?.[a] ?? a}</span>)}
        </div>
      )}
      {nesting.sinColocar.length > 0 && (
        <div className="aviso aviso--warn">
          {nesting.sinColocar.length} {nesting.sinColocar.length === 1 ? 'pieza no se pudo colocar' : 'piezas no se pudieron colocar'}: {[...new Set(nesting.sinColocar.map((s) => s.motivo))].join('; ')}
        </div>
      )}
    </div>
  );
}
